import React from "react";
import { Plus, ExternalLink, Play, Images } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import { EllipsisMenu } from "../../../../components/menu/ellipsis/EllipsisMenu";
import EmptyState from "../../../../components/feedback/EmptyState";

import ViewContainer from "../../../../components/layout/view/ViewContainer";

import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";
import ContentItem from "../../../../components/layout/content/ContentItem";

import Heading from "../../../../components/typography/Heading";
import Text from "../../../../components/typography/Text";

import "./TalentMediaView.css";

const getVideoDetails = (value) => {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    const parts = url.pathname.split("/").filter(Boolean);

    if (["youtu.be", "www.youtu.be"].includes(host)) {
      const id = parts[0];

      if (/^[A-Za-z0-9_-]{11}$/.test(id || "")) {
        return {
          provider: "YouTube",
          thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
        };
      }
    }

    if (["youtube.com", "www.youtube.com", "m.youtube.com"].includes(host)) {
      const id =
        url.pathname === "/watch"
          ? url.searchParams.get("v")
          : ["shorts", "embed", "live"].includes(parts[0])
            ? parts[1]
            : null;

      if (/^[A-Za-z0-9_-]{11}$/.test(id || "")) {
        return {
          provider: "YouTube",
          thumbnail: `https://i.ytimg.com/vi/${id}/hqdefault.jpg`,
        };
      }
    }

    if (["vimeo.com", "www.vimeo.com"].includes(host)) {
      return {
        provider: "Vimeo",
        thumbnail: null,
      };
    }
  } catch {
    return {
      provider: "External",
      thumbnail: null,
    };
  }

  return {
    provider: "External",
    thumbnail: null,
  };
};

export default function TalentMediaView({
  media = [],
  onAdd,
  onEdit,
  onRemove,
  busy = false,
  readOnly = false,
}) {
  const mediaList = Array.isArray(media) ? media : [];

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title="Media"
          subTitle="My sporting highlights and memorable moments."
        >
          {!readOnly && (
            <Button
              value="Add Media"
              icon={<Plus size={16} />}
              variant="primary-outlined"
              disabled={busy}
              onClick={onAdd}
            />
          )}
        </ContentPanelHeader>

        {!mediaList.length ? (
          <EmptyState
            icon={<Images size={28} />}
            title="No media added yet"
            description={
              readOnly
                ? "Sporting highlights and media will appear here when available."
                : "Add videos and highlights to showcase your sporting moments."
            }
          />
        ) : (
          <div className="talent-media__grid">
            {mediaList.map((item) => {
              const isExternal =
                item.mediaSource === "External" && Boolean(item.externalUrl);

              const video = isExternal
                ? getVideoDetails(item.externalUrl)
                : { provider: "Katch", thumbnail: null };

              const meta = [item.sport, video.provider, item.visibility]
                .filter(Boolean)
                .join(" · ");

              return (
                <ContentItem key={item.mediaId} className="talent-media__card">
                  {isExternal ? (
                    <a
                      className="talent-media__thumbnail"
                      href={item.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Watch ${item.title}`}
                    >
                      {video.thumbnail ? (
                        <img src={video.thumbnail} alt="" loading="lazy" />
                      ) : (
                        <div className="talent-media__placeholder">
                          <Play size={32} />
                        </div>
                      )}

                      <span className="talent-media__play">
                        <Play size={20} fill="currentColor" />
                      </span>
                    </a>
                  ) : (
                    <div className="talent-media__thumbnail">
                      <div className="talent-media__placeholder">
                        <Play size={32} />
                      </div>
                    </div>
                  )}

                  <div className="talent-media__content">
                    <div className="talent-media__heading">
                      <div className="talent-media__details">
                        <Heading as="h3" size="sm">
                          {item.title}
                        </Heading>

                        {meta && (
                          <Text as="p" tone="secondary" size="sm">
                            {meta}
                          </Text>
                        )}
                      </div>

                      {!readOnly && (
                        <EllipsisMenu
                          ariaLabel={`${item.title} options`}
                          position="right"
                          disabled={busy}
                          options={[
                            {
                              id: "edit",
                              label: "Edit Media",
                              action: () => onEdit?.(item),
                            },
                            {
                              id: "delete",
                              label: "Delete Media",
                              className: "is-danger",
                              action: () => onRemove?.(item),
                            },
                          ]}
                        />
                      )}
                    </div>

                    {item.description && (
                      <Text
                        as="p"
                        tone="secondary"
                        size="sm"
                        className="talent-media__description"
                      >
                        {item.description}
                      </Text>
                    )}

                    {isExternal && (
                      <a
                        className="talent-media__link"
                        href={item.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Watch
                        <ExternalLink size={14} />
                      </a>
                    )}
                  </div>
                </ContentItem>
              );
            })}
          </div>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
